window.AAPlaybookService = (() => {
  const supabase = () => window.AASupabaseClient.getClient();

  const PLAYBOOK_STATUS_LABELS = {
  draft: 'Draft',
  submitted: 'Submitted',
  active_editing: 'Active Editing',
  locked: 'Locked',
  reopened: 'Reopened',
  archived: 'Archived'
};

  function formatPlaybookStatus(status) {
  return PLAYBOOK_STATUS_LABELS[status] || 'Draft';
}

// ===============================
// Sprint 12 — Phase 7
// Workflow Resolver Layer
// ===============================

function getPlaybookDeadline(playbook) {
  const month = Number(playbook?.month);
  const year = Number(playbook?.year);

  if (!month || !year) return null;

  /*
   * Playbooks close on the 15th of the month BEFORE
   * the marketing month.
   *
   * Examples:
   * September 2026 -> August 15, 2026
   * January 2027 -> December 15, 2026
   */
  return new Date(year, month - 2, 15, 23, 59, 59, 999);
}

function getCurrentProductionMonth(referenceDate = new Date()) {
  const year = referenceDate.getFullYear();
  const month = referenceDate.getMonth();

  /*
   * Through the 15th:
   * current Playbook = next calendar month.
   *
   * After the 15th:
   * current Playbook = two calendar months ahead.
   */
  const monthsAhead =
    referenceDate.getDate() <= 15 ? 1 : 2;

  const targetDate =
    new Date(year, month + monthsAhead, 1);

  return {
    month: targetDate.getMonth() + 1,
    year: targetDate.getFullYear()
  };
}

function isPastPlaybookDeadline(playbook) {
  const deadline = getPlaybookDeadline(playbook);

  if (!deadline) return false;

  return new Date() > deadline;
}

function isReopenWindowActive(playbook) {
  if (!playbook) return false;

  if (playbook.reopenWindowActive === true) return true;
  if (playbook.reopen_window_active === true) return true;

  const expiresAt =
    playbook.reopenExpiresAt ||
    playbook.reopen_expires_at;

  if (!expiresAt) return false;

  return new Date(expiresAt) > new Date();
}

function resolvePlaybookStatus(playbook) {
  if (!playbook) return 'draft';

  const status = playbook.status;

  if (status === 'archived') return 'archived';

  if (status === 'reopened' && isReopenWindowActive(playbook)) {
    return 'reopened';
  }

  if (status === 'locked' || status === 'approved') {
    return 'locked';
  }

  if (isPastPlaybookDeadline(playbook)) {
    return 'locked';
  }

  if (status === 'submitted') {
    return 'submitted';
  }

  if (status === 'draft') {
    return 'draft';
  }

  return 'active_editing';
}

function applyDeadlineLock(playbook) {
  if (!playbook) return playbook;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus !== 'locked') {
    return playbook;
  }

  return {
    ...playbook,
    status: 'locked'
  };
}

function isCurrentPlaybook(playbook, context = {}) {
  if (!playbook) return false;

  if (resolvePlaybookStatus(playbook) === 'archived') return false;

  if (context.playbookId && playbook.id === context.playbookId) {
    return true;
  }

  if (
    context.clientId &&
    playbook.client_id &&
    context.clientId !== playbook.client_id
  ) {
    return false;
  }

  const playbookMonth = Number(playbook.month);
  const playbookYear = Number(playbook.year);

  const contextMonth = Number(context.month);
  const contextYear = Number(context.year);

  if (contextMonth && contextYear) {
    return playbookMonth === contextMonth && playbookYear === contextYear;
  }

  const now = new Date();

  return (
    playbookMonth === now.getMonth() + 1 &&
    playbookYear === now.getFullYear()
  );
}

function canEditPlaybook(playbook) {
  if (!playbook) {
    return false;
  }

  const resolvedStatus =
    resolvePlaybookStatus(playbook);

  return (
    resolvedStatus === 'draft' ||
    resolvedStatus === 'submitted' ||
    resolvedStatus === 'active_editing' ||
    resolvedStatus === 'reopened'
  );
}
function enterReopenedState(playbook) {
  if (!playbook) return null;

  const expiresAt = new Date();
  expiresAt.setHours(expiresAt.getHours() + 48);

  return {
    ...playbook,
    status: 'reopened',
    reopenWindowActive: true,
    reopenExpiresAt: expiresAt.toISOString()
  };
}

  // ===============================
  // Sprint 12 — Business Rules Layer
  // Phase 1: Read-only core functions
  // ===============================

  async function getPlaybookState(playbookId) {
    const db = supabase();

    if (!db || !playbookId) return null;

    const { data: playbook, error } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('id', playbookId)
      .single();

    if (error || !playbook) return null;

    if (playbook.status === 'archived') return 'archived';
    if (playbook.status === 'reopened') return 'reopened';
    if (playbook.status === 'locked' || playbook.status === 'approved') return 'locked';

    const current = await getCurrentPlaybook(playbook.client_id);

    if (current && current.id === playbook.id) return 'current';

    return 'upcoming';
  }

  async function getCurrentPlaybook(clientId) {
  const db = supabase();

  if (!db || !clientId) return null;

  const target = getCurrentProductionMonth();

  const { data, error } = await db
    .from('aa_playbooks')
    .select('*')
    .eq('client_id', clientId)
    .eq('month', target.month)
    .eq('year', target.year)
    .neq('status', 'archived')
    .maybeSingle();

  if (error || !data) return null;

  return {
    id: data.id,
    month: data.month,
    year: data.year,
    status: data.status
  };
}

  async function getPlaybookWindow(clientId) {
    const db = supabase();

    if (!db || !clientId) {
      return {
        current: null,
        upcoming: [],
        history: []
      };
    }

    const { data, error } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('client_id', clientId)
      .order('year', { ascending: true })
      .order('month', { ascending: true });

    if (error || !data?.length) {
      return {
        current: null,
        upcoming: [],
        history: []
      };
    }

    const current = await getCurrentPlaybook(clientId);
    const currentIndex = current
      ? data.findIndex((playbook) => playbook.id === current.id)
      : -1;

    const upcoming = currentIndex >= 0
      ? data.slice(currentIndex + 1, currentIndex + 6)
      : [];

    const upcomingIds = upcoming.map((playbook) => playbook.id);

    const history = data.filter((playbook) => {
      if (current && playbook.id === current.id) return false;
      if (upcomingIds.includes(playbook.id)) return false;
      return true;
    });

    return {
  current,
  nextPlaybook: upcoming[0] || null,
  upcoming,
  history
};
  }

  function getPlaybookLifecycleState(playbook) {
  return resolvePlaybookStatus(playbook);
}

function isPlaybookLocked(playbook) {
  return resolvePlaybookStatus(playbook) === 'locked';
}

function isPlaybookEditable(playbook, context = {}) {
  return canEditPlaybook(playbook, context);
}

  async function loadPlaybookById({ playbookId }) {
    const db = supabase();
    if (!db || !playbookId) return null;

    const { data: playbook, error: playbookError } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('id', playbookId)
      .single();

    if (playbookError) return null;

    const { data: fields } = await db
      .from('aa_playbook_field_values')
      .select('*')
      .eq('playbook_id', playbook.id);

    const values = {};

    (fields || []).forEach((field) => {
      values[field.field_id] = field.field_value;
    });

    return { playbook, values };
  }

  async function getPlaybookDashboardById({ playbookId }) {
    const db = supabase();
    if (!db || !playbookId) return null;

    const { data: playbook, error } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('id', playbookId)
      .single();

    if (error) return null;

    const { data: fields } = await db
      .from('aa_playbook_field_values')
      .select('updated_at')
      .eq('playbook_id', playbookId)
      .order('updated_at', { ascending: false })
      .limit(1);

    return {
      playbook,
      lastSavedAt: fields && fields.length ? fields[0].updated_at : null
    };
  }

  async function getPlaybookById({ playbookId }) {
    const db = supabase();
    if (!db || !playbookId) return null;

    const { data, error } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('id', playbookId)
      .single();

    if (error) return null;

    return data;
  }

  async function getPlaybooksByClient({ clientId }) {
    const db = supabase();
    if (!db || !clientId) return [];

    const { data, error } = await db
      .from('aa_playbooks')
      .select(`
        id,
        month,
        year,
        status,
        created_at,
        updated_at
      `)
      .eq('client_id', clientId)
      .order('year', { ascending: false })
      .order('month', { ascending: false });

    if (error) {
      console.error('Error loading previous playbooks:', error);
      return [];
    }

    return data || [];
  }

  async function getClients() {
  const db = supabase();

  if (!db) return [];

  const { data, error } = await db
    .from('aa_clients')
    .select(`
      id,
      business_name,
      active_status,
      account_status,
      invoice_status
    `)
    .order('business_name', { ascending: true });

  if (error) {
    console.error('Error loading clients:', error);
    return [];
  }

  return data || [];
}

async function updateClient({ id, businessName, activeStatus, accountStatus, invoiceStatus }) {
  const db = supabase();

  if (!db || !id) {
    return {
      success: false
    };
  }

  const { data, error } = await db
    .from('aa_clients')
    .update({
      business_name: businessName,
      active_status: activeStatus,
      account_status: accountStatus,
      invoice_status: invoiceStatus,
      updated_at: new Date().toISOString()
    })
    .eq('id', id)
    .select()
    .single();

  if (error) {
    console.error('Error updating client:', error);

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    client: data
  };
}

async function archiveClient({ id }) {
  const db = supabase();

  if (!db || !id) {
    return false;
  }

  const { error } = await db
    .from('aa_clients')
    .update({
      active_status: 'archived',
      updated_at: new Date().toISOString()
    })
    .eq('id', id);

  if (error) {
    console.error('Error archiving client:', error);
    return false;
  }

  return true;
}

async function createClient({ businessName }) {
  const db = supabase();

  if (!db || !businessName?.trim()) {
    return {
      success: false,
      error: 'Business name is required.'
    };
  }

  const { data, error } = await db
    .from('aa_clients')
    .insert({
      business_name: businessName.trim()
    })
    .select()
    .single();

  if (error) {
    console.error('Error creating client:', error);

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    client: data
  };
}

function getDefaultClientConfiguration() {
  return {
    relationships: [],
    services: [],
    serviceOptions: [],
    requiredAccessStatus: {}
  };
}

function normalizeClientConfiguration(configuration = {}) {
  return {
    ...getDefaultClientConfiguration(),
    ...configuration,
    relationships: Array.isArray(configuration.relationships) ? configuration.relationships : [],
    services: Array.isArray(configuration.services) ? configuration.services : [],
    serviceOptions: Array.isArray(configuration.serviceOptions) ? configuration.serviceOptions : [],
    requiredAccessStatus: configuration.requiredAccessStatus && typeof configuration.requiredAccessStatus === 'object'
      ? configuration.requiredAccessStatus
      : {}
  };
}

async function getClientConfiguration({ clientId }) {
  const db = supabase();

  if (!db || !clientId) {
    return getDefaultClientConfiguration();
  }

  const { data, error } = await db
    .from('aa_clients')
    .select('client_configuration')
    .eq('id', clientId)
    .single();

  if (error) {
    console.error('Error loading client configuration:', error);
    return getDefaultClientConfiguration();
  }

  return normalizeClientConfiguration(data?.client_configuration);
}

async function saveClientConfiguration({ clientId, configuration }) {
  const db = supabase();

  if (!db || !clientId) {
    return {
      success: false
    };
  }

  const cleanConfiguration = normalizeClientConfiguration(configuration);

  const { error } = await db
    .from('aa_clients')
    .update({
      client_configuration: cleanConfiguration,
      updated_at: new Date().toISOString()
    })
    .eq('id', clientId);

  if (error) {
    console.error('Error saving client configuration:', error);

    return {
      success: false,
      error
    };
  }

  return {
    success: true,
    configuration: cleanConfiguration
  };
}



async function getClientTemplateConfiguration({ clientId }) {
  const db = supabase();

  if (!db || !clientId) {
    return {
      assignment: null,
      template: null,
      sections: []
    };
  }

  const { data: assignments, error: assignmentError } =
    await db
      .from('aa_client_template_assignments')
      .select(`
        id,
        client_id,
        template_id,
        assignment_status,
        assignment_config,
        updated_at
      `)
      .eq('client_id', clientId)
      .order('updated_at', { ascending: false })
      .limit(1);

  if (assignmentError) {
    console.error(
      'Error loading client template assignment:',
      assignmentError
    );

    return {
      assignment: null,
      template: null,
      sections: []
    };
  }

  const assignment =
    assignments?.[0] || null;

  if (!assignment) {
    return {
      assignment: null,
      template: null,
      sections: []
    };
  }

  let sections =
    Array.isArray(
      assignment.assignment_config?.sections
    )
      ? assignment.assignment_config.sections
      : [];

  let template = null;

  if (assignment.template_id) {
    const { data: templateRow, error: templateError } =
      await db
        .from('aa_workspace_templates')
        .select(`
          id,
          template_name,
          template_type,
          template_config,
          is_active,
          updated_at
        `)
        .eq('id', assignment.template_id)
        .maybeSingle();

    if (templateError) {
      console.error(
        'Error loading assigned workspace template:',
        templateError
      );
    } else {
      template = templateRow || null;

      if (
        !sections.length &&
        Array.isArray(
          template?.template_config?.sections
        )
      ) {
        sections =
          template.template_config.sections;
      }
    }
  }

  return {
    assignment,
    template,
    sections
  };
}

  async function getClientAccount({ clientId }) {
    const db = supabase();
    if (!db || !clientId) return null;

    const { data, error } = await db
      .from('aa_clients')
      .select('*')
      .eq('id', clientId)
      .single();

    if (error) return null;

    return data;
  }

  async function getMarketingPlanByClient({ clientId }) {
  const db = supabase();

  if (!db || !clientId) {
    return {
      plan: null,
      sections: [],
      services: []
    };
  }

  const { data: plan, error: planError } = await db
    .from('aa_marketing_plans')
    .select('*')
    .eq('client_id', clientId)
    .order('last_updated', { ascending: false })
    .limit(1)
    .maybeSingle();

  if (planError || !plan) {
    return {
      plan: null,
      sections: [],
      services: []
    };
  }

  const { data: sections, error: sectionsError } = await db
    .from('aa_marketing_plan_sections')
    .select('*')
    .eq('plan_id', plan.id)
    .order('sort_order', { ascending: true });

  if (sectionsError) {
    return {
      plan,
      sections: [],
      services: []
    };
  }

  const sectionIds = (sections || []).map((section) => section.id);

  let services = [];

  if (sectionIds.length) {
    const { data: serviceRows, error: servicesError } = await db
      .from('aa_marketing_plan_services')
      .select('*')
      .in('section_id', sectionIds)
      .eq('active', true)
      .order('sort_order', { ascending: true });

    if (!servicesError) {
      services = serviceRows || [];
    }
  }

  return {
    plan: {
      id: plan.id,
      clientId: plan.client_id,
      name: plan.plan_name || 'Your Custom Marketing Plan',
      introText: plan.intro_text || '',
      status: plan.status || 'Active',
      lastUpdated: plan.last_updated || null
    },
    sections: (sections || []).map((section) => ({
      id: section.id,
      planId: section.plan_id,
      title: section.title,
      sortOrder: section.sort_order,
      services: services.filter((service) => service.section_id === section.id)
    })),
    services
  };
}

async function getClientMarketingPackage({ clientId, month, year }) {
  const db = supabase();

  if (!db || !clientId) {
    return {
      services: []
    };
  }

  const targetMonth = Number(month);
  const targetYear = Number(year);

  const { data: packageRows, error: packageError } = await db
    .from('aa_client_package_services')
    .select('*')
    .eq('client_id', clientId)
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('created_at', { ascending: true });

  if (packageError || !packageRows?.length) {
    return {
      services: []
    };
  }

  const serviceCatalogIds = packageRows
    .map((row) => row.service_catalog_id)
    .filter(Boolean);

  const { data: catalogRows, error: catalogError } = await db
    .from('aa_service_catalog')
    .select('*')
    .in('id', serviceCatalogIds);

  if (catalogError) {
    return {
      services: []
    };
  }

  let progressRows = [];

  if (targetMonth && targetYear) {
    const packageServiceIds = packageRows.map((row) => row.id);

    const { data: progressData, error: progressError } = await db
      .from('aa_client_package_progress')
      .select('*')
      .eq('client_id', clientId)
      .eq('month', targetMonth)
      .eq('year', targetYear)
      .in('package_service_id', packageServiceIds);

    if (!progressError) {
      progressRows = progressData || [];
    }
  }

  return {
    services: packageRows.map((row) => {
      const catalogService = (catalogRows || []).find((service) => {
        return service.id === row.service_catalog_id;
      });

      const progress = progressRows.find((item) => {
        return item.package_service_id === row.id;
      });

      const included = Number(row.quantity || 0);
      const completed = Number(progress?.completed_quantity || 0);
      const remaining = Math.max(included - completed, 0);

      return {
        id: row.id,
        serviceId: row.service_catalog_id,
        name: catalogService?.service_name || 'Service',
        description: catalogService?.service_description || '',
        category: catalogService?.category || '',
        included,
        completed,
        remaining,
        allowance: row.quantity,
        unit: row.unit_label || catalogService?.default_unit_label || 'included',
        notes: row.notes || '',
        status: row.status || 'active'
      };
    })
  };
}

async function getClientContracts({ clientId }) {
  const db = supabase();

  if (!db || !clientId) {
    return [];
  }

  const { data, error } = await db
    .from('aa_contracts')
    .select('*')
    .eq('client_id', clientId)
    .order('effective_date', { ascending: false });

  if (error || !data) {
    return [];
  }

  return data.map((contract) => ({
    id: contract.id,
    title: contract.contract_title,
    type: contract.contract_type,
    version: contract.version,
    status: contract.status,
    effectiveDate: contract.effective_date,
    expirationDate: contract.expiration_date,
    renewalDate: contract.renewal_date,
    documentReference: contract.document_reference,
    createdAt: contract.created_at,
    updatedAt: contract.updated_at
  }));
}

async function getClientBrandAssets({ clientId }) {
  const db = supabase();

  if (!db || !clientId) {
    return [];
  }

  const { data, error } = await db
    .from('aa_brand_assets')
    .select('*')
    .eq('client_id', clientId)
    .eq('is_active', true)
    .order('display_order', { ascending: true })
    .order('uploaded_at', { ascending: false });

  if (error || !data) {
    return [];
  }

  return data.map((asset) => ({
    id: asset.id,
    type: asset.asset_type,
    title: asset.asset_title,
    fileName: asset.file_name,
    storagePath: asset.storage_path,
    description: asset.description,
    uploadedAt: asset.uploaded_at,
    displayOrder: asset.display_order,
    createdAt: asset.created_at,
    updatedAt: asset.updated_at
  }));
}

  async function saveField({ playbookId, sectionId, fieldId, value }) {
    const db = supabase();

    if (!db || !playbookId || !sectionId || !fieldId) {
      return false;
    }

    const { error } = await db
      .from('aa_playbook_field_values')
      .upsert(
        {
          playbook_id: playbookId,
          section_id: sectionId,
          field_id: fieldId,
          field_value: value,
          updated_at: new Date().toISOString()
        },
        { onConflict: 'playbook_id,section_id,field_id' }
      );

    if (error) {
      console.error('Error saving field:', error);
      return false;
    }

    return true;
  }

  async function saveSection({ playbookId, sectionId, values }) {
  const saves = Object.entries(values || {}).map(([fieldId, value]) => {
    return saveField({ playbookId, sectionId, fieldId, value });
  });

  const results = await Promise.all(saves);
  return results.every(Boolean);
}

async function saveSectionCompletion({
  playbookId,
  sectionId,
  completed
} = {}) {
  if (!playbookId || !sectionId) {
    return false;
  }

  return saveField({
    playbookId,
    sectionId,
    fieldId: `__section_complete__:${sectionId}`,
    value: completed === true
  });
}

async function savePlaybookSectionCount({
  playbookId,
  totalSections
} = {}) {
  if (!playbookId) {
    return false;
  }

  const count = Number(totalSections);

  if (!Number.isInteger(count) || count < 0) {
    return false;
  }

  return saveField({
    playbookId,
    sectionId: '__playbook_progress__',
    fieldId: '__total_sections__',
    value: count
  });
}

async function getPlaybookCompletion({
  playbookId
} = {}) {
  const db = supabase();

  if (!db || !playbookId) {
    return {
      completedSections: 0,
      totalSections: 0,
      percentage: 0
    };
  }

  const { data, error } = await db
    .from('aa_playbook_field_values')
    .select('section_id, field_id, field_value')
    .eq('playbook_id', playbookId);

  if (error) {
    console.error(
      'Error loading Playbook completion:',
      error
    );

    return {
      completedSections: 0,
      totalSections: 0,
      percentage: 0
    };
  }

  const rows = data || [];

  const totalRow = rows.find(
    (row) =>
      row.section_id === '__playbook_progress__' &&
      row.field_id === '__total_sections__'
  );

  const totalSections =
    Math.max(
      Number(totalRow?.field_value || 0),
      0
    );

  const completedSections = rows.filter(
    (row) =>
      String(row.field_id || '')
        .startsWith('__section_complete__:') &&
      (
        row.field_value === true ||
        row.field_value === 'true'
      )
  ).length;

  const percentage =
    totalSections > 0
      ? Math.min(
          Math.round(
            (completedSections / totalSections) * 100
          ),
          100
        )
      : 0;

  return {
    completedSections,
    totalSections,
    percentage
  };
}

async function savePlaybook({ playbookId, values }) {
    const sectionSaves = Object.entries(values || {}).map(([sectionId, sectionValues]) => {
      return saveSection({ playbookId, sectionId, values: sectionValues });
    });

    const results = await Promise.all(sectionSaves);
    return results.every(Boolean);
  }

  function getNextPlaybookMonth(playbook) {
    const currentMonth = Number(playbook?.month);
    const currentYear = Number(playbook?.year);

    if (!currentMonth || !currentYear) return null;

    if (currentMonth === 12) {
      return {
        month: 1,
        year: currentYear + 1
      };
    }

    return {
      month: currentMonth + 1,
      year: currentYear
    };
  }

  async function createPlaybook({ clientId, month, year }) {
    const db = supabase();

    if (!db || !clientId || !month || !year) {
      return {
        success: false,
        reason: 'missing_required_fields',
        playbook: null
      };
    }

    const { data: existingPlaybook, error: existingError } = await db
      .from('aa_playbooks')
      .select('*')
      .eq('client_id', clientId)
      .eq('month', month)
      .eq('year', year)
      .maybeSingle();

    if (existingError) {
      console.error('Error checking existing playbook:', existingError);

      

      return {
        success: false,
        reason: 'check_failed',
        playbook: null
      };
    }

    if (existingPlaybook) {
      return {
        success: true,
        reason: 'already_exists',
        playbook: existingPlaybook
      };
    }

    const { data: createdPlaybook, error: createError } = await db
      .from('aa_playbooks')
      .insert({
        client_id: clientId,
        month,
        year,
        status: 'draft'
      })
      .select('*')
      .single();

    if (createError) {
      console.error('Error creating playbook:', createError);

      return {
        success: false,
        reason: 'create_failed',
        playbook: null
      };
    }

    

    return {
      success: true,
      reason: 'created',
      playbook: createdPlaybook
    };
  }

async function ensurePlaybookWindow(clientId) {
  const db = supabase();

  if (!db || !clientId) {
    return [];
  }

  /*
   * Safety:
   * Only maintain a rolling window for clients that
   * already participate in the Playbook system.
   */
  const { data: existing, error: existingError } =
    await db
      .from('aa_playbooks')
      .select('id')
      .eq('client_id', clientId)
      .limit(1);

  if (existingError || !existing?.length) {
    return [];
  }

  const current = getCurrentProductionMonth();
  const results = [];

  for (let offset = 0; offset < 6; offset += 1) {
    const targetDate = new Date(
      current.year,
      current.month - 1 + offset,
      1
    );

    const result = await createPlaybook({
      clientId,
      month: targetDate.getMonth() + 1,
      year: targetDate.getFullYear()
    });

    results.push(result);
  }

  return results;
}

async function submitPlaybook(playbookId) {
  const db = supabase();

  if (!db || !playbookId) return false;

  const { error } = await db
    .from('aa_playbooks')
    .update({
      status: 'submitted',
      updated_at: new Date().toISOString()
    })
    .eq('id', playbookId);

  return !error;
}

async function lockPlaybook(playbookId) {
  const db = supabase();

  if (!db || !playbookId) return false;

  const { error } = await db
    .from('aa_playbooks')
    .update({
      status: 'locked',
      updated_at: new Date().toISOString()
    })
    .eq('id', playbookId);

  return !error;
}

async function reopenPlaybook(playbookId) {
  const db = supabase();

  if (!db || !playbookId) return false;

  const { data: playbook, error: loadError } = await db
    .from('aa_playbooks')
    .select('*')
    .eq('id', playbookId)
    .single();

  if (loadError || !playbook) return false;

  const reopenedPlaybook = enterReopenedState(playbook);

  const { error } = await db
    .from('aa_playbooks')
    .update({
      status: reopenedPlaybook.status,
      reopen_window_active: reopenedPlaybook.reopenWindowActive,
      reopen_expires_at: reopenedPlaybook.reopenExpiresAt,
      updated_at: new Date().toISOString()
    })
    .eq('id', playbookId);

  return !error;
}

async function archivePlaybook(playbookId) {
  const db = supabase();

  if (!db || !playbookId) return false;

  const { error } = await db
    .from('aa_playbooks')
    .update({
      status: 'archived',
      updated_at: new Date().toISOString()
    })
    .eq('id', playbookId);

  return !error;
}


function getLockReason(playbook) {
  if (!playbook) return null;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus === 'archived') {
    return 'archived';
  }

  if (resolvedStatus === 'locked') {
    return 'locked';
  }

  return null;
}

function isPlaybookReopened(playbook) {
  return resolvePlaybookStatus(playbook) === 'reopened';
}

function isPlaybookArchived(playbook) {
  return resolvePlaybookStatus(playbook) === 'archived';
}

function canLockPlaybook(playbook) {
  if (!playbook) return false;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus === 'archived') return false;
  if (resolvedStatus === 'locked') return false;
  if (resolvedStatus === 'reopened') return false;

  return true;
}

function canReopenPlaybook(playbook) {
  if (!playbook) return false;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus === 'archived') return false;
  if (resolvedStatus === 'reopened') return false;

  return resolvedStatus === 'locked';
}

function canArchivePlaybook(playbook) {
  if (!playbook) return false;

  const resolvedStatus = resolvePlaybookStatus(playbook);

  if (resolvedStatus === 'archived') return false;

  return resolvedStatus === 'locked';
}

function getAllowedLifecycleActions(playbook) {
  return {
    canLock: canLockPlaybook(playbook),
    canReopen: canReopenPlaybook(playbook),
    canArchive: canArchivePlaybook(playbook)
  };
}
function getClientConfigurationFoundation() {
  return {
    relationship: null,
    relationships: [],
    relationshipOptions: [
      {
        id: 'monthly_marketing_client',
        label: 'Monthly Marketing Client',
        recommendedWorkflowStepIds: [
          'configure_relationships',
          'configure_services',
          'collect_access',
          'enable_modules',
          'generate_workspace',
          'begin_onboarding'
        ]
      },
      {
        id: 'website_build_client',
        label: 'Website Build Client',
        recommendedWorkflowStepIds: [
          'configure_relationships',
          'configure_services',
          'collect_access',
          'enable_modules',
          'generate_workspace',
          'begin_onboarding'
        ]
      },
      {
        id: 'project_client',
        label: 'Project Client',
        recommendedWorkflowStepIds: [
          'configure_relationships',
          'configure_services',
          'collect_access',
          'enable_modules',
          'generate_workspace',
          'begin_onboarding'
        ]
      }
    ],
    services: [],
    serviceCatalog: [
      {
        id: 'monthly_marketing',
        label: 'Monthly Marketing',
        serviceOptionIds: [
          'catalogs',
          'email_marketing',
          'social_media'
        ],
        requiredAccessIds: [
          'facebook',
          'instagram',
          'constant_contact',
          'rain',
          'google_business_profile'
        ],
        workflowStepIds: [
          'configure_services',
          'collect_access',
          'enable_modules'
        ]
      },
      {
        id: 'website',
        label: 'Website',
        serviceOptionIds: [
          'theme',
          'hosting',
          'maintenance'
        ],
        requiredAccessIds: [
          'shopify',
          'google_business_profile'
        ],
        workflowStepIds: [
          'configure_services',
          'collect_access',
          'generate_workspace'
        ]
      },
      {
        id: 'design',
        label: 'Design',
        serviceOptionIds: [
          'branding',
          'logo',
          'ads'
        ],
        requiredAccessIds: [
          'facebook',
          'instagram'
        ],
        workflowStepIds: [
          'configure_services',
          'collect_access',
          'enable_modules'
        ]
      }
    ],
    serviceOptions: [],
    serviceOptionCatalog: [
      {
        id: 'catalogs',
        label: 'Catalogs'
      },
      {
        id: 'email_marketing',
        label: 'Email Marketing'
      },
      {
        id: 'social_media',
        label: 'Social Media'
      },
      {
        id: 'theme',
        label: 'Theme'
      },
      {
        id: 'hosting',
        label: 'Hosting'
      },
      {
        id: 'maintenance',
        label: 'Maintenance'
      },
      {
        id: 'branding',
        label: 'Branding'
      },
      {
        id: 'logo',
        label: 'Logo'
      },
      {
        id: 'ads',
        label: 'Ads'
      }
    ],
    requiredAccess: [],
    requiredAccessCatalog: [
      {
        id: 'shopify',
        label: 'Shopify'
      },
      {
        id: 'facebook',
        label: 'Facebook'
      },
      {
        id: 'instagram',
        label: 'Instagram'
      },
      {
        id: 'constant_contact',
        label: 'Constant Contact'
      },
      {
        id: 'rain',
        label: 'Rain'
      },
      {
        id: 'google_business_profile',
        label: 'Google Business Profile'
      }
    ],
    workflowSteps: [],
    workflowPreviewSteps: [
      {
        id: 'create_client',
        label: 'Create Client',
        alwaysShow: true
      },
      {
        id: 'configure_relationships',
        label: 'Configure Relationships',
        alwaysShow: true
      },
      {
        id: 'configure_services',
        label: 'Configure Services',
        alwaysShow: true
      },
      {
        id: 'collect_access',
        label: 'Collect Access',
        alwaysShow: true
      },
      {
        id: 'enable_modules',
        label: 'Enable Modules',
        alwaysShow: true
      },
      {
        id: 'generate_workspace',
        label: 'Generate Workspace',
        alwaysShow: true
      },
      {
        id: 'begin_onboarding',
        label: 'Begin Onboarding',
        alwaysShow: true
      }
    ],
    enabledModules: [],
    internalNotes: ''
  };
}

function getPlaybookPrimaryAction(playbook) {
  const resolvedStatus = resolvePlaybookStatus(playbook);
  const canEdit = isPlaybookEditable(playbook);

  return {
    label: canEdit ? 'Continue Playbook' : 'View Playbook',
    shortLabel: canEdit ? 'Continue' : 'View',
    href: playbook?.id
      ? `/pages/workspace-current-playbook?playbook=${playbook.id}`
      : '/pages/workspace-current-playbook',
    editable: canEdit,
    status: resolvedStatus
  };
}




    return {
    loadPlaybookById,
    getPlaybookById,
    getPlaybookDashboardById,
    getPlaybooksByClient,
    getPlaybookState,
    getCurrentPlaybook,
    getPlaybookWindow,
getNextPlaybookMonth,
getCurrentProductionMonth,
ensurePlaybookWindow,
createPlaybook,

    submitPlaybook,
    lockPlaybook,
    reopenPlaybook,
    archivePlaybook,

    resolvePlaybookStatus,
    applyDeadlineLock,
    isCurrentPlaybook,
    enterReopenedState,

    canEditPlaybook,
    getLockReason,
    isPlaybookReopened,
    isPlaybookArchived,
    canLockPlaybook,
    canReopenPlaybook,
    canArchivePlaybook,
    getAllowedLifecycleActions,

    getClients,
    createClient,
    updateClient,
    archiveClient,
    getClientConfiguration,
    saveClientConfiguration,
    getClientAccount,
getClientTemplateConfiguration,
getMarketingPlanByClient,
    getClientMarketingPackage,
    getClientContracts,
    getClientBrandAssets,
    saveField,
saveSection,
saveSectionCompletion,
savePlaybookSectionCount,
getPlaybookCompletion,
savePlaybook,

    formatPlaybookStatus,
    getPlaybookLifecycleState,
    getPlaybookPrimaryAction,
    getClientConfigurationFoundation,
    isPlaybookLocked,
    isPlaybookEditable
  };
})();